// Unit conversion engine.
// Kinds: weight, volume, count.
// Cross-kind (weight <-> volume) uses ingredient density_g_per_ml.

const WEIGHT_TO_G = {
  g: 1,
  kg: 1000,
  oz: 28.3495,
  lb: 453.592,
};

const VOLUME_TO_ML = {
  ml: 1,
  l: 1000,
  L: 1000,
  tsp: 4.92892,
  tbsp: 14.7868,
  cup: 236.588,
  "fl oz": 29.5735,
};

const COUNT_TO_EACH = {
  each: 1,
  ea: 1,
  dozen: 12,
};

export const UNIT_KINDS = {
  weight: ["g", "kg", "oz", "lb"],
  volume: ["ml", "L", "tsp", "tbsp", "cup", "fl oz"],
  count: ["each", "dozen"],
};

export function unitKindOf(unit) {
  if (unit == null) return null;
  const key = String(unit).trim();
  if (WEIGHT_TO_G[key] != null) return "weight";
  if (VOLUME_TO_ML[key] != null) return "volume";
  if (COUNT_TO_EACH[key.toLowerCase()] != null) return "count";
  return null;
}

// Convert quantity from any unit into the ingredient's purchase unit.
// Returns { converted, steps: [string] } for transparency.
export function convertToPurchaseUnit({ qty, fromUnit, purchaseUnit, purchaseUnitKind, densityGPerMl }) {
  const steps = [];
  const fromKind = unitKindOf(fromUnit);
  const toKind = purchaseUnitKind || unitKindOf(purchaseUnit);

  if (!fromKind || !toKind) {
    return { converted: null, steps: ["Unknown unit"], error: `Unknown unit: ${fromUnit} or ${purchaseUnit}` };
  }

  let value = Number(qty);
  let currentUnit = fromUnit;

  if (fromKind === toKind) {
    // same-kind conversion via base unit
    if (fromKind === "weight") {
      const grams = value * WEIGHT_TO_G[fromUnit];
      steps.push(`${qty} ${fromUnit} → ${grams.toFixed(2)} g`);
      value = grams / WEIGHT_TO_G[purchaseUnit];
      steps.push(`${grams.toFixed(2)} g → ${value.toFixed(4)} ${purchaseUnit}`);
    } else if (fromKind === "volume") {
      const ml = value * VOLUME_TO_ML[fromUnit];
      steps.push(`${qty} ${fromUnit} → ${ml.toFixed(2)} ml`);
      value = ml / VOLUME_TO_ML[purchaseUnit];
      steps.push(`${ml.toFixed(2)} ml → ${value.toFixed(4)} ${purchaseUnit}`);
    } else {
      // count
      const each = value * COUNT_TO_EACH[fromUnit.toLowerCase()];
      steps.push(`${qty} ${fromUnit} → ${each} each`);
      value = each / COUNT_TO_EACH[purchaseUnit.toLowerCase()];
      steps.push(`${each} each → ${value.toFixed(4)} ${purchaseUnit}`);
    }
    return { converted: value, steps };
  }

  // cross-kind weight <-> volume via density
  if ((fromKind === "weight" && toKind === "volume") || (fromKind === "volume" && toKind === "weight")) {
    if (!densityGPerMl) {
      return { converted: null, steps: ["Density required for cross-unit conversion."], error: "Missing density" };
    }
    if (fromKind === "weight") {
      const grams = value * WEIGHT_TO_G[fromUnit];
      const ml = grams / densityGPerMl;
      steps.push(`${qty} ${fromUnit} → ${grams.toFixed(2)} g`);
      steps.push(`${grams.toFixed(2)} g ÷ ${densityGPerMl} g/ml → ${ml.toFixed(2)} ml`);
      value = ml / VOLUME_TO_ML[purchaseUnit];
      steps.push(`${ml.toFixed(2)} ml → ${value.toFixed(4)} ${purchaseUnit}`);
    } else {
      const ml = value * VOLUME_TO_ML[fromUnit];
      const grams = ml * densityGPerMl;
      steps.push(`${qty} ${fromUnit} → ${ml.toFixed(2)} ml`);
      steps.push(`${ml.toFixed(2)} ml × ${densityGPerMl} g/ml → ${grams.toFixed(2)} g`);
      value = grams / WEIGHT_TO_G[purchaseUnit];
      steps.push(`${grams.toFixed(2)} g → ${value.toFixed(4)} ${purchaseUnit}`);
    }
    return { converted: value, steps };
  }

  return { converted: null, steps: ["Incompatible unit kinds."], error: "Incompatible kinds" };
}

// Cost math for a single ingredient line in a recipe.
export function computeLineCost({ line, ingredient }) {
  const { quantity, unit } = line;

  const conv = convertToPurchaseUnit({
    qty: quantity,
    fromUnit: unit,
    purchaseUnit: ingredient.purchase_unit,
    purchaseUnitKind: ingredient.purchase_unit_kind,
    densityGPerMl: ingredient.density_g_per_ml,
  });

  if (conv.error || conv.converted == null) {
    return {
      qtyInPurchaseUnit: null,
      packFraction: 0,
      foodCost: 0,
      laborCost: 0,
      total: 0,
      steps: conv.steps,
      error: conv.error,
    };
  }

  const yieldFactor = Math.max((ingredient.yield_percent || 100) / 100, 0.0001);
  // Effective pack quantity after yield loss
  const usableQtyPerPack = ingredient.purchase_qty * yieldFactor;
  const packFraction = conv.converted / usableQtyPerPack;
  const foodCost = packFraction * ingredient.purchase_price;

  // Labor cost proportional to fraction of pack used * pack prep time
  const prepHours = (ingredient.prep_time_minutes || 0) / 60;
  const laborCost = packFraction * prepHours * (ingredient.labor_rate_per_hour || 0);

  const stepsFull = [
    ...conv.steps,
    `Yield factor: ${(yieldFactor * 100).toFixed(0)}% → usable ${usableQtyPerPack.toFixed(2)} ${ingredient.purchase_unit}/pack`,
    `Pack fraction: ${conv.converted.toFixed(4)} / ${usableQtyPerPack.toFixed(4)} = ${(packFraction * 100).toFixed(2)}%`,
    `Food cost: ${(packFraction * 100).toFixed(2)}% × ${ingredient.purchase_price} = ${foodCost.toFixed(2)}`,
    `Labor: ${(packFraction * 100).toFixed(2)}% × ${prepHours.toFixed(2)}h × ${ingredient.labor_rate_per_hour}/h = ${laborCost.toFixed(2)}`,
  ];

  return {
    qtyInPurchaseUnit: conv.converted,
    packFraction,
    foodCost,
    laborCost,
    total: foodCost + laborCost,
    steps: stepsFull,
  };
}

export function computeRecipeCost({ recipe, ingredientsById }) {
  const lines = (recipe.ingredients || []).map((line) => {
    const ingredient = ingredientsById[line.ingredient_id];
    if (!ingredient) {
      return { line, ingredient: null, cost: { foodCost: 0, laborCost: 0, total: 0, error: "Ingredient not found" } };
    }
    return { line, ingredient, cost: computeLineCost({ line, ingredient }) };
  });

  const foodCost = lines.reduce((s, l) => s + (l.cost.foodCost || 0), 0);
  let laborCost = lines.reduce((s, l) => s + (l.cost.laborCost || 0), 0);
  const servings = Math.max(recipe.servings || 1, 1);

  // Recipe-level labor override (dish-level prep_time × hourly_rate for the whole batch)
  if (recipe.prep_time_minutes != null && recipe.labor_rate_per_hour != null) {
    laborCost = (Number(recipe.prep_time_minutes) / 60) * Number(recipe.labor_rate_per_hour);
  }

  const total = foodCost + laborCost;
  const perServing = total / servings;
  const sellPrice = Number(recipe.sell_price || 0);
  const foodCostPercent = sellPrice > 0 ? (perServing / sellPrice) * 100 : 0;
  const margin = sellPrice - perServing;

  return {
    lines,
    foodCost,
    laborCost,
    total,
    perServing,
    sellPrice,
    foodCostPercent,
    margin,
  };
}
